import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  promotionalOfferListAction,
  promotionalOfferDeleteAction,
} from "../../../redux/actions/promotional-offer-action";
import successToast from "../../../components/helper-components/success-toast";
import errorToast from "../../../components/helper-components/error-toast";
import AddButton from "../../../components/buttons/button";
import DeleteIcon from "../../../assets/icons/delete-icon";
import EditPencilIcon from "../../../assets/icons/edit-pencil-icon";
import InnerLayout from "../../../components/layouts/inner-layout";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { Search } from "lucide-react";
import moment from "moment";

const tableHeading = [
  { label: "OFFER NAME", key: "po_name" },
  { label: "THRESHOLD (Rs.)", key: "po_threshold_amount" },
  { label: "FREE ITEM", key: "po_free_item_name" },
  { label: "QTY", key: "po_free_item_quantity" },
  { label: "START DATE", key: "po_start_date" },
  { label: "END DATE", key: "po_end_date" },
  { label: "STATUS", key: "po_status" },
  { label: "", key: "actions" },
];

const PromotionalOfferList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");

  const fetchOffers = () => {
    dispatch(
      promotionalOfferListAction(
        { searchValue },
        (data) => {
          if (data?.data) {
            setOffers(data.data.offerList || []);
            setTotalCount(data.data.totalCount || 0);
          }
        },
        (err) => {
          errorToast("Failed to fetch promotional offers");
        }
      )
    );
  };

  useEffect(() => {
    fetchOffers();
  }, [searchValue]);

  const handleDelete = (po_id) => {
    dispatch(
      promotionalOfferDeleteAction(
        { po_id },
        (data) => {
          successToast(data?.message || "Deleted successfully");
          fetchOffers();
        },
        (err) => {
          errorToast("Failed to delete offer");
        }
      )
    );
  };

  return (
    <InnerLayout list={true} title="Settings" module="promotional_offers">
      <div className="mx-6">
        <div className="flex flex-col gap-3 pt-1" style={{ height: "85vh" }}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 text-sml">
              <button
                className="flex items-center gap-2"
                onClick={() => navigate("/settings")}
              >
                <ChevronLeftIcon className="h-5 w-5 inline-block" />
                Discount Settings /
              </button>
              <span className="cursor-pointer">Promotional Offers</span>
            </div>

            <div className="flex items-center gap-3 pr-3">
              <div
                style={{ minWidth: "18rem", height: "2.5rem" }}
                className="border border-[var(--search-input-border)] flex justify-between items-center rounded-md"
              >
                <input
                  placeholder="Search by offer name..."
                  className="table_search_input px-3 py-2 !border-none !outline-none !ring-0 text-[length:var(--search-label-font-size)] w-full"
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e?.target?.value)}
                />
                <span className="px-2">
                  <Search size="16" color="#9CA3AF" />
                </span>
              </div>

              <AddButton
                onclkFn={() => navigate("/settings/promotional-offers/add")}
                text="Add Offer"
              />
            </div>
          </div>

          <div className="overflow-auto rounded-xl table-container border border-gray-200">
            <table className="w-full">
              <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
                <tr className="relative h-[50px]">
                  {tableHeading.map((heading) => (
                    <th
                      key={heading.key}
                      className="text-start px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]"
                    >
                      {heading.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {offers.map((offer, index) => (
                  <tr
                    key={offer.po_id}
                    className="hover:bg-[var(--create-table-row-hover-bg)] border-b border-[var(--create-table-border)]"
                  >
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px] py-3">
                      {offer.po_name}
                    </td>
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px] py-3">
                      {offer.po_threshold_amount}
                    </td>
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px] py-3">
                      <div>
                        <span>{offer.po_free_item_name}</span>
                        {offer.po_free_item_code && (
                          <span className="text-xs text-gray-400 ml-1">
                            ({offer.po_free_item_code})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px] py-3">
                      {offer.po_free_item_quantity}
                    </td>
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px] py-3">
                      {moment(offer.po_start_date).format("DD-MM-YYYY")}
                    </td>
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px] py-3">
                      {moment(offer.po_end_date).format("DD-MM-YYYY")}
                    </td>
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px] py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          offer.po_status
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {offer.po_status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px] py-3">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/settings/promotional-offers/edit?po_id=${offer.po_id}`
                            )
                          }
                        >
                          <EditPencilIcon />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(offer.po_id)}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {offers.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-10 text-[var(--input-place-color)] text-sm"
                    >
                      No promotional offers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </InnerLayout>
  );
};

export default PromotionalOfferList;
